// components/AdvisoryServices.jsx
import { useContext, useEffect, useState } from "react"
import { AppContext } from "../App"
import { getInlineLoader } from "../Helpers";
import Footer from "../ones/Footer";

import { 
  IoMdTrendingUp, 
  IoMdPeople, 
  IoMdSettings, 
  IoMdSchool, 
  IoMdBriefcase,
  IoMdCalendar,
  IoMdArrowDropright,
  IoMdCall,
  IoMdAnalytics,
  IoMdListBox,
  IoMdCheckbox
} from "react-icons/io";
import { FaRegHandshake, FaFileSignature, FaUserTie, FaTasks } from "react-icons/fa";

export default function AdvisoryServices(props) {
  const appContext = useContext(AppContext);
  const [ready, setReady] = useState(false);

  // Grouping the advisory services from the provided ATRIC document
  const advisoryPillars = [
    {
      id: 1,
      title: "Financial & Business Advisory",
      icon: <IoMdAnalytics />,
      description: "Strategic analysis of financial statements for decision making, business process re-engineering, and sourcing of funds.",
      features: ["Due Diligence", "Sourcing of Funds/Loans", "Business Proposals"],
      accent: "#1e293b",
      bgLight: "#f1f5f9"
    },
    {
      id: 2,
      title: "Fund & Project Management",
      icon: <FaTasks />,
      description: "Comprehensive oversight of project implementation, monitoring, evaluation, and expenditure tracking.",
      features: ["Project Monitoring", "Expenditure Tracking", "Implementation Oversight"],
      accent: "#d97706",
      bgLight: "#fff7ed"
    },
    {
      id: 3,
      title: "Secretarial & Administrative",
      icon: <FaFileSignature />,
      description: "Coordinating board meetings, company registrations (BRELA), and Facilitating acquisition exemption certificates.",
      features: ["Board Paper Preparation", "BRELA Filings", "NGO Registration"],
      accent: "#2563eb",
      bgLight: "#eff6ff"
    },
    {
      id: 4,
      title: "Special HRM Tasks",
      icon: <FaUserTie />,
      description: "Outsourced human resource management including payroll, salary mapping, recruitment, and staff appraisal.",
      features: ["Payroll Management", "Staff Recruitment", "Building Staff Morale"],
      accent: "#059669",
      bgLight: "#f0fdf4"
    }
  ];

  async function init() {
    setReady(false);
    setTimeout(() => setReady(true), 300);
  }

  useEffect(() => { init(); }, []);

  if (!ready) return (
    <div className="container d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
      {getInlineLoader()}
    </div>
  );

  return (
    <div className="bg-white">
      {/* ================= HERO SECTION ================= */}
      <section className="py-5 border-bottom" style={{ background: "linear-gradient(to right, #f8fafc, #ffffff)" }}>
        <div className="container py-5">
          <div className="row align-items-center">
            <div className="col-lg-7">
              <span className="badge mb-3 px-3 py-2 fw-bold text-uppercase" 
                    style={{ background: "#16207c", color: "#ffffff", fontSize: "11px", letterSpacing: "1px" }}>
                Strategic Advisory
              </span>
              <h1 className="display-4 fw-bold text-dark mb-4">
                Empowering Business <br />
                <span style={{ color: "#2100a3" }}>Through Expertise.</span>
              </h1>
              <p className="lead text-muted mb-5 border-start border-4 ps-4 py-2" style={{ maxWidth: "600px" }}>
                ATRIC Consultants provides a holistic range of advisory services, from process re-engineering to specialized HRM tasks, ensuring your operations remain lean and competitive.
              </p>
              <div className="d-flex gap-3">
                <button 
                  className="btn btn-dark btn-lg px-4 rounded-0 shadow-sm"
                  onClick={() => appContext.navTo({ item: 'contact_us', service: 'Advisory' })}
                >
                  Request Advisory
                </button>
                <a href="#training" className="btn btn-outline-dark btn-lg px-4 rounded-0">Explore Training</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= SERVICES GRID ================= */}
      <section className="py-5 bg-white">
        <div className="container py-5">
          <div className="row g-4">
            {advisoryPillars.map((pillar) => (
              <div className="col-lg-6" key={pillar.id}>
                <div className="card h-100 border-0 shadow-sm transition-up p-4" 
                     style={{ borderRadius: "16px", background: "#ffffff", border: "1px solid #f1f5f9" }}>
                  <div className="d-flex align-items-start gap-4">
                    <div className="p-3 rounded-4" style={{ backgroundColor: pillar.bgLight, color: pillar.accent }}>
                      {pillar.icon}
                    </div>
                    <div>
                      <h4 className="fw-bold mb-2">{pillar.title}</h4>
                      <p className="text-secondary small mb-3">{pillar.description}</p>
                      <div className="d-flex flex-wrap gap-2">
                        {pillar.features.map((tag, i) => (
                          <span key={i} className="badge bg-light text-dark fw-normal border px-2 py-1">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= TAILORED TRAINING (Point E) ================= */}
      <section id="training" className="py-5" style={{ background: "#f8fafc" }}>
        <div className="container py-4">
          <div className="row align-items-center">
            <div className="col-lg-5 mb-4 mb-lg-0">
              <div className="p-4 bg-white border rounded-4 shadow-sm">
                <IoMdSchool size={40} className="text-primary mb-3" />
                <h3 className="fw-bold">Tailored Training</h3>
                <p className="text-muted">Specialized capacity building for your team across technical and management domains.</p>
                <ul className="list-unstyled">
                  {["IFRS & IPSAS Standards", "Taxation & Financial Management", "Internal Standards on Auditing (ISA)"].map((t, i) => (
                    <li key={i} className="d-flex align-items-center gap-2 mb-2 fw-medium">
                      <IoMdCheckbox className="text-success" /> {t}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="col-lg-7">
              <div className="ps-lg-5">
                <h2 className="fw-bold mb-4">Bridging the Knowledge Gap</h2>
                <p className="text-secondary mb-4">
                  Beyond consultation, we believe in sustainable growth. Our tailored training programs are designed to equip your staff with the latest in international reporting standards and efficient business management practices.
                </p>
                <button className="btn btn-outline-dark px-4" onClick={() => appContext.navTo({ item: 'contact_us' })}>
                  View Training Schedule <IoMdArrowDropright />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= FINAL CALL TO ACTION ================= */}
      <section className="py-5 bg-white">
        <div className="container py-5">
          <div className="p-5 text-center bg-dark text-white rounded-4 shadow-lg position-relative overflow-hidden">
            <div className="position-relative z-1">
              <h2 className="display-6 fw-bold mb-3">Ready to Re-engineer Your Process?</h2>
              <p className="opacity-75 mb-4 mx-auto" style={{ maxWidth: "600px" }}>
                Partner with ATRIC for strategic business advisory and administrative excellence.
              </p>
              <div className="d-flex justify-content-center gap-3">
                <button className="btn btn-primary btn-lg px-5 fw-bold rounded-pill" onClick={() => appContext.navTo({ item: 'contact_us' })}>
                  Talk to a Consultant
                </button>
                <a href="tel:+255717918808" className="btn btn-outline-light btn-lg px-4 rounded-pill d-flex align-items-center gap-2">
                  <IoMdCall /> Call Us
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}